import React, {useEffect, useState, useReducer} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {usePromiseTracker} from 'react-promise-tracker';
import _ from 'lodash';

import Utility from '../../../util/utilityfunctions';
import {labels} from '../../../util/utils';
import {oversightTabs, UserType, projectSessionKeys, promiseAreas, routes} from '../../../util/uiconstants';

import {setOversightActiveTab} from '../../../actions/Project/projectAction';
import {getAllClients} from '../../../actions/Clients/clientsaction';

import {clearItem} from '../../Common/localStorage';

import OutputText from '@ey/canvascoreservices/OutputText';

import ContentSwitcher from '../../CoreWrappers/ContentSwitcher';
import ProgressLoader from '../../CoreWrappers/ProgressLoader';
import Search from '../../CoreWrappers/Search';
import ProjectTile from '../../Common/ProjectTile';
import StyledProjectLayout from './StyledProjectLayout';
import ActiveProjects from './ActiveProjects';
import OtherProjects from './OtherProjects';
import CompletedProjects from './CompletedProjects';
import LinkTooltip from './LinkTooltip';
import TemplateFullPage from '../../Templates/TemplateFullPage';

const reducer = (state) => state + 1;

const Projects = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [searchString, setSearchString] = useState('');

	const allProjects = useSelector((state) => state.allProjects);
	const userPreferences = useSelector((state) => state.userPreferences);
	const oversightActiveTab = useSelector((state) => state.oversightActiveTab);

	const {promiseInProgress} = usePromiseTracker({area: promiseAreas.USER_PROJECTS_AREA});
	const [counterState, counterDispatch] = useReducer(reducer, 1);

	const dispatch = useDispatch();

	const isInternalUser = userPreferences.userType.toUpperCase() === UserType.Member.toUpperCase();
	const isActiveProjects = oversightActiveTab === oversightTabs.activeProjects;
	const isOtherProjects = oversightActiveTab === oversightTabs.otherProjects;
	const isCompletedProjects = oversightActiveTab === oversightTabs.completedProjects;

	const activeProjectsTab = {
		id: oversightTabs.activeProjects,
		label: isInternalUser ? labels.myActiveProjects : labels.activeProjects
	};
	const otherActiveProjectsTab = {id: oversightTabs.otherProjects, label: labels.otherActiveProjects};
	const completedProjectsTab = {
		id: oversightTabs.completedProjects,
		label: isInternalUser ? labels.myCompletedProjects : labels.completedProjects
	};
	let oversightProjectTabs = [activeProjectsTab, completedProjectsTab];
	if (isInternalUser) oversightProjectTabs = [activeProjectsTab, otherActiveProjectsTab, completedProjectsTab];

	useEffect(() => {
		const hideOnScroll = _.debounce(counterDispatch, 100, {
			leading: true,
			trailing: false
		});
		const scrollable = document.querySelector('.customScrollbar');
		scrollable.addEventListener('scroll', hideOnScroll);
		return () => {
			scrollable.removeEventListener('scroll', hideOnScroll);
		};
	}, []);

	useEffect(() => {
		if (isInternalUser) dispatch(getAllClients(true));
		return () => {
			const projectDashboardSessionKey = `${projectSessionKeys.projectdetail}`;
			Object.keys(sessionStorage)
				.filter((key) => {
					return key.startsWith(projectDashboardSessionKey);
				})
				.forEach(function(key) {
					clearItem(key);
				});
		};
	}, []);

	const handlerOnClearSearch = () => {
		setSearchString('');
	};

	const handlerOnSearch = (value) => {
		setSearchString(value);
	};

	const renderTitle = () => <OutputText h1>{labels.welcomeLabel}</OutputText>;

	const renderTabBar = () => {
		const props = {
			options: oversightProjectTabs,
			value: oversightProjectTabs.find(({id}) => id === oversightActiveTab),
			onChange: ({id}) => dispatch(setOversightActiveTab(id))
		};

		return <ContentSwitcher {...props} />;
	};

	const renderInstructional = () => (
		<OutputText light>
			{isOtherProjects
				? labels.otherProjectsInstructionalText
				: isCompletedProjects &&
				  _.size(
						_.filter(allProjects, (p) => {
							return p.status === 'completed';
						})
				  ) === 0 &&
				  !promiseInProgress
				? Utility.createRichTextElement(labels.completedNotProjectPlaceholder)
				: isCompletedProjects
				? Utility.createRichTextElement(labels.completedProjectsInstructionalText)
				: labels.activeProjectsInstructionalText}
		</OutputText>
	);

	const renderGroupOversightProject = () => {
		const props = {
			btnLabel: labels.groupOversightProject,
			tooltipLabel: labels.groupOversightProjectTooltip,
			linkTo: `/${routes.project.root}/${routes.project.gra}`
		};

		return <LinkTooltip {...props} />;
	};

	const renderOversightProject = () => {
		const props = {
			btnLabel: labels.oversightProject,
			tooltipLabel: labels.oversightProjectTooltip,
			linkTo: `/${routes.project.root}/${routes.project.classic}`,
			variant: 'secondary'
		};
		return <LinkTooltip {...props} />;
	};

	const renderToolbar = () => (
		<section className="filter-search-wrapper">
			<section className="filter-search-wrap">
				<Search
					placeholder={labels.search}
					value={searchString}
					searchHoverText={'search'}
					onChange={handlerOnSearch}
					onClear={handlerOnClearSearch}
				/>
			</section>
			{isInternalUser && isActiveProjects && (
				<div className="oversight-project-actions">
					{renderGroupOversightProject()}
					{renderOversightProject()}
				</div>
			)}
		</section>
	);

	const renderProject = (project, index) => {
		const props = {project, isActiveProjects, isOtherProjects, isCompletedProjects, setIsLoading, counterState};

		return <ProjectTile key={index} {...props} />;
	};

	const renderProjects = () => {
		let Component = ActiveProjects;
		if (isOtherProjects) Component = OtherProjects;
		if (isCompletedProjects) Component = CompletedProjects;

		const props = {
			isSearching: searchString !== '',
			emptyMessage: labels.noResultsFound,
			renderProject,
			projects: allProjects?.filter(({name}) => name?.toLowerCase()?.includes(searchString?.toLowerCase()))
		};

		return <Component {...props} />;
	};

	return (
		<TemplateFullPage>
			{isLoading && <ProgressLoader fullscreen />}
			<StyledProjectLayout>
				{renderTitle()}
				{renderTabBar()}
				{isInternalUser && renderInstructional()}
				{renderToolbar()}
				{renderProjects()}
			</StyledProjectLayout>
		</TemplateFullPage>
	);
};

export default Projects;
